import { useEffect, useState } from "react";
import usePageMeta from "../hooks/usePageMeta";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LabelList
} from "recharts";
import { BASE_URL } from "../config";
import Layout from "./Layout";
import PeriodFilterBar, { resolvePeriodRange, defaultPeriodFilterValue } from "../components/dashboard/filters/PeriodFilterBar";
import OrderDrilldownFlow from "../components/dashboard/orders/OrderDrilldownFlow";
import WorkerPositionDrilldown from "../components/dashboard/workers/WorkerPositionDrilldown";
import RevenueGauge from "../components/dashboard/revenue/RevenueGauge";
import SalaryTrendCard from "../components/dashboard/salary/SalaryTrendCard";
import { ArrowUpRight, TrendingUp, TrendingDown, Loader2 } from "lucide-react";
import { useAppPermissions } from "../context/PermissionContext";
import '../Styles/Personnel.css';
import { formatAppIntlLabel, getAppDisplayTimezone } from '../utils/appDateTime';

// "CeylonX Test Division" -- QA-only data on the live system. The backend already leaves it out of
// every dashboard chart/card (utils/dashboardDivisionExclusions.js); this just hides it from the
// Division filter so it can't be picked.
const QA_TEST_DIVISION_IDS = [1608143];

const Dashboard = () => {
  usePageMeta("Dashboard");
  const { canSeeRevenue, loaded: permissionsLoaded } = useAppPermissions();
  const showFinancialCards = permissionsLoaded && canSeeRevenue;

  // Constants and Helpers inside component to ensure scope
  const COLORS = {
    green: '#4ade80',
    red: '#ef4444',
    blue: '#3b82f6',
    orange: '#f97316',
    yellow: '#facc15',
    gray: '#6b7280',
    purple: '#a855f7'
  };

  const generateNextWeekPlanningData = () => {
    const today = new Date();
    const nextWeekData = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() + i);
      const dayName = formatAppIntlLabel(date, { weekday: 'short', timeZone: getAppDisplayTimezone() });
      const monthName = formatAppIntlLabel(date, { month: 'short', timeZone: getAppDisplayTimezone() });
      const dayNumber = date.getDate();
      const formattedDate = `${dayName} ${monthName} ${dayNumber}`;
      const sampleValues = [
        { Confirmed: 180, Tentative: 20, Unassigned: 15, Unfilled: 5 },
        { Confirmed: 45, Tentative: 85, Unassigned: 10, Unfilled: 3 },
        { Confirmed: 65, Tentative: 8, Unassigned: 12, Unfilled: 4 },
        { Confirmed: 45, Tentative: 15, Unassigned: 25, Unfilled: 6 },
        { Confirmed: 8, Tentative: 5, Unassigned: 18, Unfilled: 2 },
        { Confirmed: 12, Tentative: 8, Unassigned: 22, Unfilled: 3 },
        { Confirmed: 10, Tentative: 6, Unassigned: 15, Unfilled: 2 },
      ];
      nextWeekData.push({
        date: formattedDate,
        ...sampleValues[i % sampleValues.length]
      });
    }
    return nextWeekData;
  };

  const generatePastWeekActualsData = () => {
    const today = new Date();
    const pastWeekData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = formatAppIntlLabel(date, { weekday: 'short', timeZone: getAppDisplayTimezone() });
      const monthName = formatAppIntlLabel(date, { month: 'short', timeZone: getAppDisplayTimezone() });
      const dayNumber = date.getDate();
      const formattedDate = `${dayName} ${monthName} ${dayNumber}`;
      const sampleValues = [
        { AsScheduled: 100, DifferentTimes: 25, NoShow: 8, NotSubmitted: 0, Unassigned: 0 },
        { AsScheduled: 0, DifferentTimes: 45, NoShow: 15, NotSubmitted: 0, Unassigned: 0 },
        { AsScheduled: 65, DifferentTimes: 35, NoShow: 0, NotSubmitted: 140, Unassigned: 0 },
        { AsScheduled: 75, DifferentTimes: 40, NoShow: 0, NotSubmitted: 150, Unassigned: 0 },
        { AsScheduled: 85, DifferentTimes: 45, NoShow: 0, NotSubmitted: 160, Unassigned: 0 },
        { AsScheduled: 65, DifferentTimes: 50, NoShow: 0, NotSubmitted: 180, Unassigned: 0 },
        { AsScheduled: 0, DifferentTimes: 0, NoShow: 0, NotSubmitted: 200, Unassigned: 0 },
      ];
      pastWeekData.push({
        date: formattedDate,
        ...sampleValues[6 - i]
      });
    }
    return pastWeekData;
  };

  const sampleActualsData = generatePastWeekActualsData();
  const samplePlanningData = generateNextWeekPlanningData();

  // Existing states
  const [planningData, setPlanningData] = useState(generateNextWeekPlanningData());
  const [planningMeta, setPlanningMeta] = useState(null);
  const [actualsData, setActualsData] = useState(generatePastWeekActualsData());
  const [actualsMeta, setActualsMeta] = useState(null);
  const [showSample, setShowSample] = useState(false);
  const [userCountry, setUserCountry] = useState("");

  // New dashboard states
  const [summaryData, setSummaryData] = useState(null);
  const [workerInsights, setWorkerInsights] = useState(null);
  const [shiftsByDivision, setShiftsByDivision] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState(null);
  const [revenuePerEmployer, setRevenuePerEmployer] = useState(null);
  const [workersBySkills, setWorkersBySkills] = useState([]);
  const [workersBySkillsMeta, setWorkersBySkillsMeta] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [orderAllocation, setOrderAllocation] = useState(null);
  const [activeDrilldown, setActiveDrilldown] = useState(null); // null | 'filled' | 'unfilled'
  const [activeWorkerPosition, setActiveWorkerPosition] = useState(null); // null | position name string

  // Per-card loading flags for the period-filter-driven fetches below, so the client can see
  // the dashboard is actually re-querying after changing the date filter (previously nothing
  // indicated a fetch was in flight -- stale numbers just sat there until new data quietly
  // swapped in, indistinguishable from the filter not having worked at all).
  const [workerInsightsLoading, setWorkerInsightsLoading] = useState(false);
  const [shiftsByDivisionLoading, setShiftsByDivisionLoading] = useState(false);
  const [revenueTrendLoading, setRevenueTrendLoading] = useState(false);
  const [revenuePerEmployerLoading, setRevenuePerEmployerLoading] = useState(false);
  const [workersBySkillsLoading, setWorkersBySkillsLoading] = useState(false);
  const [orderAllocationLoading, setOrderAllocationLoading] = useState(false);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [actualsLoading, setActualsLoading] = useState(false);

  // Filters
  const [periodFilter, setPeriodFilter] = useState(defaultPeriodFilterValue());
  const periodRange = resolvePeriodRange(periodFilter);
  const selectedDepartment = periodFilter.divisionId;

  // Shifts Planning Status / Historic Actuals charts: on narrow screens there isn't
  // room for 7 "17 August"-style X-axis labels, so Recharts' default overlap-avoidance
  // silently skips every other one. Below 640px, switch to a day-only "17" format and
  // force every tick to render instead of letting Recharts drop half of them.
  const [isNarrowScreen, setIsNarrowScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const update = () => setIsNarrowScreen(window.innerWidth < 640);
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  // Exact full-body person icons for Worker Structure (provided as SVG source, not lucide) —
  // scaled by height, aspect ratio preserved from their native viewBoxes.
  const MaleIcon = ({ height = 64 }) => (
    <svg width={(height * 30) / 80} height={height} viewBox="0 0 30 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 0C20.5228 0 25 4.47719 25 10C25 15.5228 20.5228 20 15 20C9.47719 20 5 15.5228 5 10C5 4.47719 9.47719 0 15 0ZM22.5 22.5H20.725C17.1764 24.1317 12.9766 24.2022 9.275 22.5H7.5C3.35781 22.5 0 25.8578 0 30V51.25C0 53.3211 1.67891 55 3.75 55H6.25V76.25C6.25 78.3211 7.92891 80 10 80H20C22.0711 80 23.75 78.3211 23.75 76.25V55H26.25C28.3211 55 30 53.3211 30 51.25V30C30 25.8578 26.6422 22.5 22.5 22.5Z" fill="#41EF7E" />
    </svg>
  );
  const FemaleIcon = ({ height = 64 }) => (
    <svg width={(height * 58) / 80} height={height} viewBox="0 0 58 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M58 46.5455C58 47.7576 57.5771 48.7879 56.7313 49.6364C55.8854 50.4849 54.8583 50.9091 53.65 50.9091C52.1094 50.9091 50.901 50.2576 50.025 48.9545L39.7391 33.4545H37.7V39.4545L48.8922 58.1364C49.1641 58.5909 49.3 59.0909 49.3 59.6364C49.3 60.4242 49.013 61.1061 48.4391 61.6818C47.8651 62.2576 47.1854 62.5455 46.4 62.5455H37.7V74.9091C37.7 76.303 37.2016 77.5 36.2047 78.5C35.2078 79.5 34.0146 80 32.625 80H25.375C23.9854 80 22.7922 79.5 21.7953 78.5C20.7984 77.5 20.3 76.303 20.3 74.9091V62.5455H11.6C10.8146 62.5455 10.1349 62.2576 9.56094 61.6818C8.98698 61.1061 8.7 60.4242 8.7 59.6364C8.7 59.0909 8.83594 58.5909 9.10781 58.1364L20.3 39.4545V33.4545H18.2609L7.975 48.9545C7.09896 50.2576 5.89062 50.9091 4.35 50.9091C3.14167 50.9091 2.11458 50.4849 1.26875 49.6364C0.422917 48.7879 0 47.7576 0 46.5455C0 45.6667 0.241667 44.8636 0.725 44.1364L12.325 26.6818C14.5302 23.4394 17.1885 21.8182 20.3 21.8182H37.7C40.8115 21.8182 43.4698 23.4394 45.675 26.6818L57.275 44.1364C57.7583 44.8636 58 45.6667 58 46.5455ZM39.15 10.1818C39.15 13 38.1607 15.4015 36.182 17.3864C34.2034 19.3712 31.8094 20.3636 29 20.3636C26.1906 20.3636 23.7966 19.3712 21.818 17.3864C19.8393 15.4015 18.85 13 18.85 10.1818C18.85 7.36364 19.8393 4.96212 21.818 2.97727C23.7966 0.992424 26.1906 0 29 0C31.8094 0 34.2034 0.992424 36.182 2.97727C38.1607 4.96212 39.15 7.36364 39.15 10.1818Z" fill="#EF2C44" />
    </svg>
  );

  // Order-allocation KPI card icons, exact SVG source provided
  const TotalOrdersIcon = ({ size = 20 }) => (
    <svg width={(size * 27) / 36} height={size} viewBox="0 0 27 36" fill="none" xmlns="http://www.w3.org/2000/svg">
      <mask id="mask0_46_329" style={{ maskType: 'luminance' }} maskUnits="userSpaceOnUse" x="0" y="0" width="27" height="36">
        <path d="M24.3333 4.33331H2.66667C1.74619 4.33331 1 5.07951 1 5.99998V32.6666C1 33.5871 1.74619 34.3333 2.66667 34.3333H24.3333C25.2538 34.3333 26 33.5871 26 32.6666V5.99998C26 5.07951 25.2538 4.33331 24.3333 4.33331Z" fill="white" stroke="white" strokeWidth="2" strokeLinejoin="round" />
        <path d="M8.5 1V6M18.5 1V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6.83301 13.5H20.1663M6.83301 20.1667H16.833M6.83301 26.8333H13.4997" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </mask>
      <g mask="url(#mask0_46_329)">
        <path d="M-6.5 -2.33331H33.5V37.6667H-6.5V-2.33331Z" fill="#1A73E8" />
      </g>
    </svg>
  );
  const FilledOrdersIcon = ({ size = 20 }) => (
    <svg width={(size * 25) / 35} height={size} viewBox="0 0 25 35" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M6.25 2.65652e-06C6.05373 -9.27651e-06 5.86022 0.0461963 5.68512 0.134879C5.51003 0.223561 5.35828 0.352228 5.24216 0.510462C5.12604 0.668696 5.04882 0.85205 5.01674 1.04568C4.98465 1.23931 4.99862 1.43777 5.0575 1.625L5.3325 2.5H1.25C0.918479 2.5 0.600537 2.6317 0.366117 2.86612C0.131696 3.10054 0 3.41848 0 3.75V33.75C0 34.0815 0.131696 34.3995 0.366117 34.6339C0.600537 34.8683 0.918479 35 1.25 35H23.75C24.0815 35 24.3995 34.8683 24.6339 34.6339C24.8683 34.3995 25 34.0815 25 33.75V3.75C25 3.41848 24.8683 3.10054 24.6339 2.86612C24.3995 2.6317 24.0815 2.5 23.75 2.5H19.67L19.945 1.625C20.0039 1.43757 20.0179 1.23889 19.9857 1.04506C19.9534 0.85124 19.876 0.667741 19.7596 0.509453C19.6432 0.351164 19.4911 0.222545 19.3157 0.13403C19.1403 0.0455153 18.9465 -0.000402383 18.75 2.65652e-06H6.25ZM7.95 2.5H17.05L16.2675 5H8.7325L7.95 2.5ZM12.135 24.635L20.885 15.885L19.1175 14.115L11.25 21.9825L7.135 17.865L5.365 19.635L10.365 24.635C10.4811 24.7514 10.6191 24.8438 10.7709 24.9068C10.9228 24.9698 11.0856 25.0022 11.25 25.0022C11.4144 25.0022 11.5772 24.9698 11.7291 24.9068C11.8809 24.8438 12.0189 24.7514 12.135 24.635Z" fill="#00C445" />
    </svg>
  );
  const UnfilledOrdersIcon = ({ size = 20 }) => (
    <svg width={(size * 25) / 35} height={size} viewBox="0 0 25 35" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M5.265 0.48C5.38195 0.330442 5.53143 0.209489 5.7021 0.12632C5.87277 0.0431513 6.06014 -4.81494e-05 6.25 4.02729e-08H18.75C18.9399 5.24905e-05 19.1272 0.0433587 19.2979 0.126632C19.4685 0.209905 19.6179 0.330957 19.7348 0.480599C19.8517 0.630241 19.9329 0.804541 19.9724 0.990269C20.0118 1.176 20.0084 1.36827 19.9625 1.5525L19.725 2.5H23.75C24.0815 2.5 24.3995 2.6317 24.6339 2.86612C24.8683 3.10054 25 3.41848 25 3.75V33.75C25 34.0815 24.8683 34.3995 24.6339 34.6339C24.3995 34.8683 24.0815 35 23.75 35H1.25C0.918479 35 0.600537 34.8683 0.366117 34.6339C0.131696 34.3995 0 34.0815 0 33.75V3.75C0 3.41848 0.131696 3.10054 0.366117 2.86612C0.600537 2.6317 0.918479 2.5 1.25 2.5H5.275L5.0375 1.5525C4.99108 1.36838 4.98722 1.17611 5.02621 0.990279C5.06519 0.804447 5.1485 0.629939 5.265 0.48ZM17.15 2.5H7.85L8.475 5H16.525L17.15 2.5ZM20 27.5H5V25H20V27.5ZM7.5 22.5L11.25 21.25L18.75 13.75L16.25 11.25L8.75 18.75L7.5 22.5Z" fill="#EB5656" />
    </svg>
  );
  const UnfilledRateIcon = ({ size = 20 }) => (
    <svg width={size} height={size} viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fillRule="evenodd" clipRule="evenodd" d="M2.44 2.44C0 4.88333 0 8.81 0 16.6667C0 24.5233 0 28.4517 2.44 30.8917C4.88333 33.3333 8.81 33.3333 16.6667 33.3333C24.5233 33.3333 28.4517 33.3333 30.8917 30.8917C33.3333 28.4533 33.3333 24.5233 33.3333 16.6667C33.3333 8.81 33.3333 4.88167 30.8917 2.44C28.4533 0 24.5233 0 16.6667 0C8.81 0 4.88167 0 2.44 2.44ZM9.21667 12.45C8.97971 12.2292 8.6663 12.109 8.34246 12.1147C8.01863 12.1204 7.70965 12.2516 7.48063 12.4806C7.25161 12.7097 7.12042 13.0186 7.11471 13.3425C7.10899 13.6663 7.2292 13.9797 7.45 14.2167L11.2717 18.0383C11.8186 18.5849 12.5601 18.8919 13.3333 18.8919C14.1065 18.8919 14.8481 18.5849 15.395 18.0383L18.0383 15.395C18.077 15.3562 18.123 15.3254 18.1736 15.3044C18.2243 15.2834 18.2785 15.2726 18.3333 15.2726C18.3881 15.2726 18.4424 15.2834 18.493 15.3044C18.5436 15.3254 18.5896 15.3562 18.6283 15.395L21.9817 18.75H20.8333C20.5018 18.75 20.1839 18.8817 19.9494 19.1161C19.715 19.3505 19.5833 19.6685 19.5833 20C19.5833 20.3315 19.715 20.6495 19.9494 20.8839C20.1839 21.1183 20.5018 21.25 20.8333 21.25H25C25.3315 21.25 25.6495 21.1183 25.8839 20.8839C26.1183 20.6495 26.25 20.3315 26.25 20V15.8333C26.25 15.5018 26.1183 15.1839 25.8839 14.9495C25.6495 14.715 25.3315 14.5833 25 14.5833C24.6685 14.5833 24.3505 14.715 24.1161 14.9495C23.8817 15.1839 23.75 15.5018 23.75 15.8333V16.9833L20.395 13.6283C19.8481 13.0818 19.1065 12.7748 18.3333 12.7748C17.5601 12.7748 16.8186 13.0818 16.2717 13.6283L13.6283 16.2717C13.5896 16.3105 13.5436 16.3413 13.493 16.3623C13.4424 16.3833 13.3881 16.3941 13.3333 16.3941C13.2785 16.3941 13.2243 16.3833 13.1736 16.3623C13.123 16.3413 13.077 16.3105 13.0383 16.2717L9.21667 12.45Z" fill="#FF5B00" />
    </svg>
  );

  // Display-only reformat of the backend's "Sun Aug 10, 2026" date label into "10 August" for the
  // Shifts Planning Status chart's X-axis — doesn't touch the underlying date/data at all.
  const MONTH_NAMES = { Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December' };
  const formatShiftsAxisDate = (label) => {
    const match = /^\w+ (\w{3}) (\d{1,2}),/.exec(label || '');
    if (!match) return label;
    const [, monAbbr, day] = match;
    return `${day} ${MONTH_NAMES[monAbbr] || monAbbr}`;
  };
  // Narrow-screen variant: day number only ("17"), short enough that Recharts can
  // fit all 7 ticks without needing to skip every other one.
  const formatShiftsAxisDateCompact = (label) => {
    const match = /^\w+ \w{3} (\d{1,2}),/.exec(label || '');
    return match ? match[1] : label;
  };

  const getRangeLabel = (meta, data) => {
    if (meta?.rangeFrom && meta?.rangeTo) {
      return `${meta.rangeFrom} – ${meta.rangeTo}`;
    }
    if (!Array.isArray(data) || data.length === 0) return "";
    const first = data[0]?.date ?? "";
    const last = data[data.length - 1]?.date ?? "";
    if (!first && !last) return "";
    if (first === last) return `${first}`;
    return `${first} – ${last}`;
  };

  const CustomTooltip = ({ payload, label }) => {
    if (!payload || payload.length === 0) return null;
    const data = payload.reduce((acc, cur) => {
      if (cur && cur.name != null && typeof cur.value === "number") {
        acc[cur.name] = cur.value;
      }
      return acc;
    }, {});
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    return (
      <div className="p-2 bg-white shadow-md rounded text-sm">
        <strong>{label}</strong>
        {Object.entries(data).map(([key, value]) => (
          <div key={key}>{key}: {value}</div>
        ))}
        <div className="mt-1 border-t pt-1">Total: {total}</div>
      </div>
    );
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Short "$40K"/"$1.2M" form for chart value labels on narrow screens, where a full
  // "$40,285"-style label per data point doesn't leave enough room to avoid overlapping
  // its neighbors.
  const formatCompactCurrency = (value) => {
    const n = Number(value) || 0;
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `$${Math.round(n / 1000)}K`;
    return `$${Math.round(n)}`;
  };

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  // Fetch summary cards. Revenue (inside this response) now follows the same period/division
  // filter as the rest of the dashboard -- Workers/Clients/Jobs cards are unaffected, their own
  // backend queries never read these params, they just ride along on the same request.
  useEffect(() => {
    const summaryParams = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') summaryParams.append('department', selectedDepartment);

    fetch(`${BASE_URL}/api/dashboard/summary?${summaryParams}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => {
        if (!res.ok) {
          console.error('Summary response not OK:', res.status);
          return res.json().then(err => { throw new Error(err.message || 'Request failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (data.success) {
          console.log('Summary data:', data.data);
          setSummaryData(data.data);
        } else {
          console.error('Summary failed:', data.message);
        }
      })
      .catch(err => console.error('Summary fetch error:', err));
  }, [selectedDepartment, periodRange.startDate, periodRange.endDate]);

  // Fetch worker insights
  useEffect(() => {
    // Guards against out-of-order responses: rapid PeriodFilterBar navigation fires a new
    // fetch on every click without cancelling the previous one, so a slower earlier request
    // can resolve *after* a faster later one and overwrite fresh data with stale data (this
    // is what caused different cards to show different date ranges after quick navigation).
    let ignore = false;
    const rangeYear = periodRange.startDate ? Number(periodRange.startDate.slice(0, 4)) : new Date().getFullYear();
    const rangeMonth = periodFilter.periodType === 'monthly' && periodRange.startDate
      ? Number(periodRange.startDate.slice(5, 7))
      : null;
    const params = new URLSearchParams({ year: rangeYear });
    if (rangeMonth) params.append('month', rangeMonth);
    if (selectedDepartment !== 'all') params.append('department', selectedDepartment);

    setWorkerInsightsLoading(true);
    fetch(`${BASE_URL}/api/dashboard/worker-insights?${params}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => {
        if (!res.ok) {
          console.error('Worker insights response not OK:', res.status);
          return res.json().then(err => { throw new Error(err.message || 'Request failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        if (data.success) {
          console.log('Worker insights data:', data.data);
          setWorkerInsights(data.data);
        } else {
          console.error('Worker insights failed:', data.message);
        }
      })
      .catch(err => { if (!ignore) console.error('Worker insights fetch error:', err); })
      .finally(() => { if (!ignore) setWorkerInsightsLoading(false); });
    return () => { ignore = true; };
  }, [periodFilter]);

  // Fetch shifts by division (always all divisions, ignores the global Division Filter)
  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    setShiftsByDivisionLoading(true);
    fetch(`${BASE_URL}/api/dashboard/shifts-by-division?${params}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => {
        if (!res.ok) {
          console.error('Shifts by division response not OK:', res.status);
          return res.json().then(err => { throw new Error(err.message || 'Request failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        if (data.success) {
          setShiftsByDivision(data.data || []);
        } else {
          console.error('Shifts by division failed:', data.message);
        }
      })
      .catch(err => { if (!ignore) console.error('Shifts by division fetch error:', err); })
      .finally(() => { if (!ignore) setShiftsByDivisionLoading(false); });
    return () => { ignore = true; };
  }, [periodRange.startDate, periodRange.endDate]);

  // Fetch revenue trend (Super Admin role only)
  useEffect(() => {
    if (!permissionsLoaded || !canSeeRevenue) return;
    let ignore = false;

    const revenueParams = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') revenueParams.append('department', selectedDepartment);

    setRevenueTrendLoading(true);
    fetch(`${BASE_URL}/api/dashboard/revenue-trend?${revenueParams}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => {
        if (!res.ok) {
          console.error('Revenue trend response not OK:', res.status);
          return res.json().then(err => { throw new Error(err.message || 'Request failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        if (data.success) {
          console.log('Revenue trend data:', data.data);
          setRevenueTrend(data.data);
        } else {
          console.error('Revenue trend failed:', data.message);
        }
      })
      .catch(err => { if (!ignore) console.error('Revenue trend fetch error:', err); })
      .finally(() => { if (!ignore) setRevenueTrendLoading(false); });
    return () => { ignore = true; };
  }, [permissionsLoaded, canSeeRevenue, selectedDepartment, periodRange.startDate, periodRange.endDate]);

  // Fetch revenue per employer (Super Admin role only)
  useEffect(() => {
    if (!permissionsLoaded || !canSeeRevenue) return;
    let ignore = false;

    const params = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') params.append('department', selectedDepartment);

    setRevenuePerEmployerLoading(true);
    fetch(`${BASE_URL}/api/dashboard/revenue-per-employer?${params}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => res.json())
      .then(data => {
        if (ignore) return;
        if (data.success) setRevenuePerEmployer(data.data);
        else console.error('Revenue per employer failed:', data.message);
      })
      .catch(err => { if (!ignore) console.error('Revenue per employer fetch error:', err); })
      .finally(() => { if (!ignore) setRevenuePerEmployerLoading(false); });
    return () => { ignore = true; };
  }, [permissionsLoaded, canSeeRevenue, selectedDepartment, periodRange.startDate, periodRange.endDate]);

  // Fetch workers by skills — now follows the global PeriodFilterBar date range (previously
  // ignored it entirely and always queried a fixed trailing-24-months window, so this chart
  // never had a "Showing data for" line and didn't move when the header's date/division changed).
  useEffect(() => {
    let ignore = false;
    const skillsParams = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') skillsParams.append('department', selectedDepartment);

    setWorkersBySkillsLoading(true);
    fetch(`${BASE_URL}/api/dashboard/workers-by-skills?${skillsParams}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => {
        if (!res.ok) {
          console.error('Workers by skills response not OK:', res.status);
          return res.json().then(err => { throw new Error(err.message || 'Request failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (ignore) return;
        if (data.success) {
          console.log('Workers by skills data:', data.data);
          setWorkersBySkills(data.data || []);
          setWorkersBySkillsMeta(data.meta ?? null);
        } else {
          console.error('Workers by skills failed:', data.message);
        }
      })
      .catch(err => { if (!ignore) console.error('Workers by skills fetch error:', err); })
      .finally(() => { if (!ignore) setWorkersBySkillsLoading(false); });
    return () => { ignore = true; };
  }, [periodRange.startDate, periodRange.endDate, selectedDepartment]);

  // Fetch order allocation summary (Total/Filled/Unfilled Orders + Rate)
  useEffect(() => {
    let ignore = false;
    const params = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') params.append('department', selectedDepartment);

    setOrderAllocationLoading(true);
    fetch(`${BASE_URL}/api/dashboard/order-allocation-summary?${params}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => res.json())
      .then(data => {
        if (ignore) return;
        if (data.success) setOrderAllocation(data.data);
      })
      .catch(err => { if (!ignore) console.error('Order allocation summary fetch error:', err); })
      .finally(() => { if (!ignore) setOrderAllocationLoading(false); });
    return () => { ignore = true; };
  }, [periodRange.startDate, periodRange.endDate, selectedDepartment]);

  // Fetch divisions for filter
  useEffect(() => {
    fetch(`${BASE_URL}/api/divisions`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setDivisions(data.data || []);
      })
      .catch(err => console.error('Divisions fetch error:', err));
  }, []);

  // Shifts Planning Status — now follows the global PeriodFilterBar range + Division Filter
  // (previously used its own fixed "today ± week" range via ?range=week, completely independent
  // of the global filter — that was the mismatch reported between the header's selected week and
  // this chart's "Showing data for" line).
  useEffect(() => {
    if (showSample) {
      setPlanningMeta(null);
      setPlanningData(samplePlanningData);
      return;
    }
    let ignore = false;
    const params = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') params.append('department', selectedDepartment);
    setPlanningLoading(true);
    fetch(`${BASE_URL}/api/shifts/planning?${params}`)
      .then(res => res.json())
      .then(resp => {
        if (ignore) return;
        const data = Array.isArray(resp?.data) ? resp.data : [];
        setPlanningMeta(resp?.meta ?? null);
        setPlanningData(data);
      })
      .catch(() => {
        if (ignore) return;
        setPlanningMeta(null);
        setPlanningData([]);
      })
      .finally(() => { if (!ignore) setPlanningLoading(false); });
    return () => { ignore = true; };
  }, [showSample, periodRange.startDate, periodRange.endDate, selectedDepartment]);

  // Historic Actuals Recorded Status — same fix as Shifts Planning Status above.
  useEffect(() => {
    if (showSample) {
      setActualsMeta(null);
      setActualsData(sampleActualsData);
      return;
    }
    let ignore = false;
    const params = new URLSearchParams({ startDate: periodRange.startDate, endDate: periodRange.endDate });
    if (selectedDepartment !== 'all') params.append('department', selectedDepartment);
    setActualsLoading(true);
    fetch(`${BASE_URL}/api/shifts/actuals?${params}`)
      .then(res => res.json())
      .then(resp => {
        if (ignore) return;
        const data = Array.isArray(resp?.data) ? resp.data : [];
        if (data.length === 0) {
          setActualsMeta(resp?.meta ?? null);
          setActualsData([]);
        } else {
          setActualsMeta(resp?.meta ?? null);
          setActualsData(data);
        }
      })
      .catch(() => {
        if (ignore) return;
        setActualsMeta(null);
        setActualsData([]);
      })
      .finally(() => { if (!ignore) setActualsLoading(false); });
    return () => { ignore = true; };
  }, [showSample, periodRange.startDate, periodRange.endDate, selectedDepartment]);

  useEffect(() => {
    fetch(`${BASE_URL}/api/my-account/account/me`)
      .then(res => res.json())
      .then(json => {
        const c = json?.data?.country || json?.data?.region || "";
        setUserCountry(c);
      })
      .catch(() => setUserCountry(""));
  }, []);

  // Prepare chart data
  const workerStructureData = workerInsights ? [
    { name: 'Male', value: workerInsights.workerStructure?.male || 0, fill: COLORS.green },
    { name: 'Female', value: workerInsights.workerStructure?.female || 0, fill: COLORS.red },
    { name: 'Unknown', value: workerInsights.workerStructure?.unknown || 0, fill: '#5292F9' }
  ].filter(item => item.value > 0) : [];

  const divisionColors = [
    '#4ade80', '#ef4444', '#fb7185', '#3b82f6', '#111827', '#facc15',
    '#d946ef', '#312e81', '#7f1d1d', '#166534', '#2dd4bf', '#a855f7'
  ];
  const shiftsByDivisionData = shiftsByDivision.map((item, index) => ({
    ...item,
    fill: divisionColors[index % divisionColors.length]
  }));

  const totalOrders = orderAllocation?.totalOrders || 0;
  const filledOrdersCount = orderAllocation?.filledOrders || 0;
  const unfilledOrdersCount = orderAllocation?.unfilledOrders || 0;
  const filledOrdersPercentage = totalOrders > 0 ? Math.round((filledOrdersCount / totalOrders) * 100) : 0;
  const unfilledOrdersPercentage = totalOrders > 0 ? Math.round((unfilledOrdersCount / totalOrders) * 100) : 0;

  // Total vs Cancelled: a separate breakdown from Filled/Unfilled above. `totalOrders` here is the
  // shared /order-allocation-summary field (Filled + Unfilled, excludes cancelled -- Unfilled Rate
  // above depends on that meaning), so `requestedOrdersCount` is the true "every order requested"
  // count this card actually displays as Total Orders (fixed 2026-09-08: was showing `totalOrders`,
  // which undercounted by exactly the cancelled count).
  const cancelledOrdersCount = orderAllocation?.cancelledOrders || 0;
  const requestedOrdersCount = totalOrders + cancelledOrdersCount;
  const totalOrdersPercentage = requestedOrdersCount > 0 ? Math.round((totalOrders / requestedOrdersCount) * 100) : 0;
  const cancelledOrdersPercentage = requestedOrdersCount > 0 ? Math.round((cancelledOrdersCount / requestedOrdersCount) * 100) : 0;

  // Custom XAxis tick: recharts' angle/textAnchor shorthand doesn't reliably keep rotated text
  // below the baseline, so this explicitly anchors the text a fixed gap under the baseline (x,y)
  // and rotates it 90° counter-clockwise around that same point, making it hang straight down.
  const renderPositionTick = ({ x, y, payload }) => (
    <text x={x} y={y + 10} textAnchor="end" fontSize={11} fill="#6b7280" transform={`rotate(-90, ${x}, ${y + 10})`}>
      {payload.value}
    </text>
  );

  return (
    <Layout active="Dashboard">
      <div className="flex-1 flex flex-col bg-gray-50">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Dashboard</h1>
              <p className="text-sm text-gray-500">
                {formatAppIntlLabel(new Date(), { month: 'short', day: 'numeric', year: 'numeric', timeZone: getAppDisplayTimezone() })}
                {userCountry ? ` ${userCountry}` : ''}
              </p>
            </div>
          </div>
        </header>

        <div className="flex-1 p-6 space-y-6 overflow-y-auto">
          {/* Summary Cards */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${
              showFinancialCards ? "lg:grid-cols-4" : "lg:grid-cols-3"
            }`}
          >
            {/* Total Workers */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Workers</h3>
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {summaryData?.workers?.total?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600">
                <div>Active Workers: {summaryData?.workers?.active || 0}</div>
                <div>Active Percentage: {summaryData?.workers?.activePercentage || 0}%</div>
              </div>
            </div>

            {/* Total Clients */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Total Clients</h3>
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {summaryData?.clients?.total?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600">
                <div>Active Clients: {summaryData?.clients?.active || 0}</div>
                <div>Active Percentage: {summaryData?.clients?.activePercentage || 0}%</div>
              </div>
            </div>

            {/* Active Jobs */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-gray-600">Active Jobs</h3>
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">
                {summaryData?.jobs?.current?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-gray-600">
                <div className={summaryData?.jobs?.changeType === 'increase' ? 'text-red-600' : 'text-green-600'}>
                  Last Period: {summaryData?.jobs?.change || 0}%
                </div>
                <div>Last Period: {summaryData?.jobs?.previous?.toLocaleString() || '0'}</div>
              </div>
            </div>

            {showFinancialCards && (
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-medium text-gray-600">Revenue</h3>
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
                {summaryData?.revenue?.currentRange?.from && (
                  <div className="text-xs text-gray-400 mb-3">
                    {summaryData.revenue.currentRange.from} – {summaryData.revenue.currentRange.to}
                  </div>
                )}
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {formatCurrency(summaryData?.revenue?.current || 0)}
                </div>
                <div className="text-sm text-gray-600">
                  <div className={summaryData?.revenue?.changeType === 'increase' ? 'text-green-600' : 'text-red-600'}>
                    From Last Period: {summaryData?.revenue?.change >= 0 ? '+' : ''}{summaryData?.revenue?.change || 0}%
                  </div>
                  <div>
                    Last Period: {formatCurrency(summaryData?.revenue?.previous || 0)}
                    {summaryData?.revenue?.previousRange?.from && (
                      <span className="text-gray-400"> ({summaryData.revenue.previousRange.from} – {summaryData.revenue.previousRange.to})</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Worker Insights Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="mb-6">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold text-gray-900">Worker Insights</h2>
                {workerInsightsLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
              </div>
              <p className="text-sm text-gray-500 mt-1">This dashboard presents a comprehensive overview of key employee metrics.</p>
            </div>

            {/* First Row: Number of Workers and Worker Structure */}
            <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6 mb-6 items-stretch">
              {/* Number of Workers */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <h3 className="text-base font-semibold text-gray-900 px-5 pt-4 pb-3">Number of Workers</h3>
                <div className="border-t border-gray-200" />
                <div className="px-5 pt-3 pb-4">
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-3xl font-bold text-gray-900 leading-none">
                      {workerInsights?.numberOfWorkers?.current || 0}
                    </span>
                    <span className={`flex items-center gap-0.5 text-sm font-medium leading-none ${workerInsights?.numberOfWorkers?.trend < 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {workerInsights?.numberOfWorkers?.trend < 0 ? <TrendingDown size={16} /> : <TrendingUp size={16} />}
                      {Math.abs(workerInsights?.numberOfWorkers?.trend || 0)}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">Monthly Employee Count Trend</p>
                  <ResponsiveContainer width="100%" height={170}>
                    <BarChart data={workerInsights?.numberOfWorkers?.monthly || []} margin={{ top: 4, left: 0, right: 0, bottom: 0 }}>
                      <XAxis
                        dataKey="month"
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                        tick={{ fontSize: isNarrowScreen ? 8 : 10, fill: '#6b7280' }}
                        interval={isNarrowScreen ? 0 : undefined}
                        tickFormatter={isNarrowScreen ? (label) => String(label || '').split(' ')[0] : undefined}
                      />
                      <YAxis hide domain={[0, 'dataMax']} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#00BFFF" radius={[5, 5, 0, 0]} barSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Worker Structure */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden flex flex-col">
                <h3 className="text-base font-semibold text-gray-900 px-5 pt-4 pb-3">Worker Structure</h3>
                <div className="border-t border-gray-200" />
                <div className="px-5 pt-4 pb-3 flex-1 flex flex-col">
                  <div className="flex items-center justify-center gap-2 sm:gap-3 flex-1">
                    {/* Male */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <MaleIcon height={60} />
                      <div className="text-2xl font-bold text-green-600 mt-2">{workerInsights?.workerStructure?.male || 0}%</div>
                      <div className="text-xs text-gray-500">({workerInsights?.workerStructure?.maleCount || 0})</div>
                    </div>

                    {/* Donut */}
                    <div className="flex-shrink-0" style={{ width: 'clamp(120px, 40vw, 200px)', height: 'clamp(120px, 40vw, 200px)' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={workerStructureData.length > 0 ? workerStructureData : [{ name: 'No Data', value: 100, fill: '#e5e7eb' }]}
                            cx="50%"
                            cy="50%"
                            innerRadius="65%"
                            outerRadius="95%"
                            dataKey="value"
                          >
                            {(workerStructureData.length > 0 ? workerStructureData : [{ name: 'No Data', value: 100, fill: '#e5e7eb' }]).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Pie>
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Female */}
                    <div className="flex flex-col items-center flex-shrink-0">
                      <FemaleIcon height={60} />
                      <div className="text-2xl font-bold text-red-600 mt-2">{workerInsights?.workerStructure?.female || 0}%</div>
                      <div className="text-xs text-gray-500">({workerInsights?.workerStructure?.femaleCount || 0})</div>
                    </div>
                  </div>
                  {/* Unknown/Other — bottom-right, no divider */}
                  {workerInsights?.workerStructure?.unknown > 0 && (
                    <div className="flex items-center justify-end gap-2 mt-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#5292F9' }}></span>
                      <span className="text-sm text-gray-500">Unknown / Other</span>
                      <span className="text-lg font-bold text-gray-900">{workerInsights.workerStructure.unknown}%</span>
                      <span className="text-xs text-gray-400">({workerInsights.workerStructure.unknownCount || 0})</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-4 flex justify-end">
            <PeriodFilterBar
              value={periodFilter}
              onChange={setPeriodFilter}
              divisions={divisions.filter(
                (d) =>
                  !["Office", "iTrain"].includes(d.name) &&
                  !QA_TEST_DIVISION_IDS.includes(Number(d.division_id)),
              )}
            />
          </div>

          {/* Order Allocation stat cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Total Orders</h3>
                <TotalOrdersIcon size={20} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{totalOrders.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Filled Orders</h3>
                <FilledOrdersIcon size={20} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{filledOrdersCount.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Unfilled Orders</h3>
                <UnfilledOrdersIcon size={20} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{unfilledOrdersCount.toLocaleString()}</div>
            </div>

            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-600">Unfilled Rate</h3>
                <UnfilledRateIcon size={20} />
              </div>
              <div className="text-2xl font-bold text-gray-900">{orderAllocation?.unfilledRate ?? 0}%</div>
            </div>
          </div>

          {/* Order Allocation Rate */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 overflow-hidden flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Order Allocation Rate</h2>
              {orderAllocationLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
            </div>
            <div className="border-t border-gray-200 pt-[66px] flex-1 flex">
              <div className="flex flex-col sm:flex-row items-stretch gap-8 flex-1">
                <div className="flex-[3] flex items-center justify-center py-6">
                  <div style={{ width: 160, height: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Filled Orders', value: filledOrdersCount, fill: COLORS.green },
                          { name: 'Unfilled Orders', value: unfilledOrdersCount, fill: COLORS.red }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={58}
                        outerRadius={78}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                      >
                        <Cell fill={COLORS.green} />
                        <Cell fill={COLORS.red} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  </div>
                </div>

                <div className="hidden sm:block w-px bg-gray-200" />

                <div className="flex flex-col flex-[2] w-full">
                  <div className="flex items-start gap-6 pb-4 border-b border-gray-100">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3 h-3 rounded-full bg-green-500 shrink-0"></span>
                        <span className="font-semibold text-gray-900">Filled Orders</span>
                      </div>
                      <div className="text-xs text-gray-500">Percentage : {filledOrdersPercentage}%</div>
                      <div className="text-xs text-gray-500">No of Orders : {filledOrdersCount.toLocaleString()}</div>
                    </div>
                    <button type="button" onClick={() => setActiveDrilldown('filled')} className="flex items-center gap-1 text-green-600 text-sm font-medium hover:underline shrink-0">
                      View All <ArrowUpRight size={16} className="border border-green-600 rounded-full p-0.5" />
                    </button>
                  </div>

                  <div className="flex items-start gap-6 pt-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3 h-3 rounded-full bg-red-500 shrink-0"></span>
                        <span className="font-semibold text-gray-900">Unfilled Orders</span>
                      </div>
                      <div className="text-xs text-gray-500">Percentage : {unfilledOrdersPercentage}%</div>
                      <div className="text-xs text-gray-500">No of Orders : {unfilledOrdersCount.toLocaleString()}</div>
                    </div>
                    <button type="button" onClick={() => setActiveDrilldown('unfilled')} className="flex items-center gap-1 text-red-600 text-sm font-medium hover:underline shrink-0">
                      View All <ArrowUpRight size={16} className="border border-red-600 rounded-full p-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Total & Cancelled Orders -- a separate card from Order Allocation Rate above, per
              client request. Total Orders here is `requestedOrdersCount` (every order requested
              in the period, cancelled or not) -- fixed 2026-09-08, was displaying `totalOrders`
              (Filled + Unfilled only), which undercounted by exactly the cancelled count. The
              donut still plots the non-cancelled/cancelled split (`totalOrders`/`cancelledOrdersCount`)
              since those two slices are what actually sum to the whole. */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 overflow-hidden flex flex-col">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Total & Cancelled Orders</h2>
            <div className="border-t border-gray-200 pt-[66px] flex-1 flex">
              <div className="flex flex-col sm:flex-row items-stretch gap-8 flex-1">
                <div className="flex-[3] flex items-center justify-center py-6">
                  <div style={{ width: 160, height: 160 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Total Orders', value: totalOrders, fill: '#6366f1' },
                          { name: 'Cancelled Orders', value: cancelledOrdersCount, fill: COLORS.orange }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={58}
                        outerRadius={78}
                        startAngle={90}
                        endAngle={-270}
                        dataKey="value"
                      >
                        <Cell fill="#6366f1" />
                        <Cell fill={COLORS.orange} />
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  </div>
                </div>

                <div className="hidden sm:block w-px bg-gray-200" />

                <div className="flex flex-col flex-[2] w-full">
                  <div className="flex items-start gap-6 pb-4 border-b border-gray-100">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: '#6366f1' }}></span>
                        <span className="font-semibold text-gray-900">Total Orders</span>
                      </div>
                      <div className="text-xs text-gray-500">Percentage : {totalOrdersPercentage}%</div>
                      <div className="text-xs text-gray-500">No of Orders : {requestedOrdersCount.toLocaleString()}</div>
                    </div>
                    <button type="button" onClick={() => setActiveDrilldown('total')} className="flex items-center gap-1 text-sm font-medium hover:underline shrink-0" style={{ color: '#6366f1' }}>
                      View All <ArrowUpRight size={16} className="border rounded-full p-0.5" style={{ borderColor: '#6366f1' }} />
                    </button>
                  </div>

                  <div className="flex items-start gap-6 pt-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-3 h-3 rounded-full bg-orange-500 shrink-0"></span>
                        <span className="font-semibold text-gray-900">Cancelled Orders</span>
                      </div>
                      <div className="text-xs text-gray-500">Percentage : {cancelledOrdersPercentage}%</div>
                      <div className="text-xs text-gray-500">No of Orders : {cancelledOrdersCount.toLocaleString()}</div>
                    </div>
                    <button type="button" onClick={() => setActiveDrilldown('cancelled')} className="flex items-center gap-1 text-orange-600 text-sm font-medium hover:underline shrink-0">
                      View All <ArrowUpRight size={16} className="border border-orange-600 rounded-full p-0.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {activeDrilldown && (
            <OrderDrilldownFlow type={activeDrilldown} onClose={() => setActiveDrilldown(null)} getAuthHeaders={getAuthHeaders} initialPeriodFilter={periodFilter} />
          )}

          {activeWorkerPosition && (
            <WorkerPositionDrilldown position={activeWorkerPosition} onClose={() => setActiveWorkerPosition(null)} getAuthHeaders={getAuthHeaders} initialPeriodFilter={periodFilter} />
          )}

          {/* Shifts by Division */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Shifts by Division</h2>
              {shiftsByDivisionLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
            </div>
            <div className="flex flex-col lg:flex-row items-stretch gap-8">
              <div style={{ height: 220 }} className="w-full flex items-center justify-center lg:flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={shiftsByDivisionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={65}
                      outerRadius={100}
                      dataKey="value"
                      nameKey="name"
                    >
                      {shiftsByDivisionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name, props) => [
                      showFinancialCards && props.payload.revenue !== undefined
                        ? `${value} (${props.payload.percentage}%) • ${formatCurrency(props.payload.revenue)} (${props.payload.revenuePercentage}%)`
                        : `${value} (${props.payload.percentage}%)`,
                      name
                    ]} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              <div className="hidden lg:block w-px bg-gray-200" />

              <div className="flex flex-col justify-center gap-2 w-full lg:flex-1">
                {shiftsByDivisionData.slice(0, Math.ceil(shiftsByDivisionData.length / 2)).map((entry, index) => (
                  <div key={index} className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.fill }}></span>
                    <span className="text-gray-800 font-medium">{entry.name}</span>
                    <span className="text-gray-500 whitespace-nowrap">
                      ({entry.value.toLocaleString()} | {entry.percentage}%)
                      {showFinancialCards && entry.revenue !== undefined && (
                        <> ({formatCurrency(entry.revenue)} | {entry.revenuePercentage}%)</>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="hidden lg:block w-px bg-gray-200" />

              <div className="flex flex-col justify-center gap-2 w-full lg:flex-1">
                {shiftsByDivisionData.slice(Math.ceil(shiftsByDivisionData.length / 2)).map((entry, index) => (
                  <div key={index} className="flex items-center flex-wrap gap-x-2 gap-y-0.5 text-sm">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: entry.fill }}></span>
                    <span className="text-gray-800 font-medium">{entry.name}</span>
                    <span className="text-gray-500 whitespace-nowrap">
                      ({entry.value.toLocaleString()} | {entry.percentage}%)
                      {showFinancialCards && entry.revenue !== undefined && (
                        <> ({formatCurrency(entry.revenue)} | {entry.revenuePercentage}%)</>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {showFinancialCards && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Revenue</h2>
                {revenueTrendLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="text-2xl font-bold text-gray-900">
                  {formatCurrency(revenueTrend?.total || 0)}
                </div>
                <p className="text-sm text-gray-500 mb-4">Monthly Revenue Trend</p>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueTrend?.daily || []} margin={{ top: 24, left: 0, right: 8 }}>
                    <defs>
                      <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} interval={isNarrowScreen ? 0 : undefined} />
                    <YAxis hide domain={['dataMin - 500', 'dataMax + 500']} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke={COLORS.blue}
                      strokeWidth={2}
                      fill="url(#revenueFill)"
                      dot={{ r: 4, fill: COLORS.blue, strokeWidth: 0 }}
                    >
                      <LabelList
                        dataKey="revenue"
                        position="top"
                        offset={12}
                        formatter={isNarrowScreen ? formatCompactCurrency : (value) => `$${Math.round(value).toLocaleString()}`}
                        style={{ fontSize: isNarrowScreen ? 9 : 12, fill: '#374151' }}
                      />
                    </Area>
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {showFinancialCards && (
            <div className="bg-white rounded-lg border border-gray-200 p-6 w-full lg:max-w-3xl">
              <div className="flex items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Revenue Per Employer</h2>
                {revenuePerEmployerLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
              </div>
              <div className="border-t border-gray-200 pt-4">
                <div className="text-3xl font-bold text-gray-900 mb-4">
                  {formatCurrency(revenuePerEmployer?.totalRevenue || 0)}
                </div>
                <div className="flex flex-col sm:flex-row items-center gap-10">
                  <div style={{ width: 260, height: 230 }} className="shrink-0">
                    <RevenueGauge percentage={revenuePerEmployer?.salesAchievement || 0} />
                  </div>
                  <div className="space-y-4 text-base flex-1 w-full">
                    <div className="flex justify-between sm:justify-start sm:gap-2">
                      <span className="font-medium text-gray-800">Total Employers :</span>
                      <span className="text-gray-500">{(revenuePerEmployer?.totalEmployers || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between sm:justify-start sm:gap-2">
                      <span className="font-medium text-gray-800">Total Revenue :</span>
                      <span className="text-gray-500">{formatCurrency(revenuePerEmployer?.totalRevenue || 0)}</span>
                    </div>
                    <div className="flex justify-between sm:justify-start sm:gap-2">
                      <span className="font-medium text-gray-800">Revenue Per Employer:</span>
                      <span className="text-gray-500">{formatCurrency(revenuePerEmployer?.revenuePerEmployer || 0)}</span>
                    </div>
                    <div className="flex justify-between sm:justify-start sm:gap-2">
                      <span className="font-medium text-gray-800">Expected Revenue Per Employer:</span>
                      <span className="text-gray-500">{formatCurrency(revenuePerEmployer?.expectedRevenuePerEmployer || 0)}</span>
                    </div>
                    <div className="flex justify-between sm:justify-start sm:gap-2">
                      <span className="font-medium text-gray-800">Sales Achievement:</span>
                      <span className="text-gray-500">{revenuePerEmployer?.salesAchievement ?? 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showFinancialCards && <SalaryTrendCard getAuthHeaders={getAuthHeaders} divisionId={selectedDepartment} />}

          {/* Workers By Positions (backend field is still "skill" — data already comes from the positions table).
              Shows two bars per position: Total Shifts (excl. cancelled) and Assigned Shifts (confirmed/assigned
              worker on that shift) -- previously a single distinct-worker headcount bar. */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-gray-900">Shifts By Positions</h2>
              {workersBySkillsLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
            </div>
            <div className="flex items-center gap-6 mb-3">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#00C445' }}></span> Total Shifts
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#1A73E8' }}></span> Assigned Shifts
              </div>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <div className="flex items-baseline gap-6">
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {workersBySkills.reduce((sum, item) => sum + (item.totalShifts || 0), 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-500">Total Shifts</p>
                </div>
                <div>
                  <div className="text-2xl font-bold text-gray-900">
                    {workersBySkills.reduce((sum, item) => sum + (item.assignedShifts || 0), 0).toLocaleString()}
                  </div>
                  <p className="text-xs text-gray-500">Assigned Shifts</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-2 mt-1">
                Showing data for: {getRangeLabel(workersBySkillsMeta, null)} · Division: {selectedDepartment === 'all' ? 'All' : (divisions.find((d) => String(d.division_id) === String(selectedDepartment))?.name || selectedDepartment)}
              </p>
              <ResponsiveContainer width="100%" height={340}>
                <BarChart data={workersBySkills} margin={{ top: 24, left: 0, right: 0, bottom: 5 }} barCategoryGap="20%" barGap={2}>
                  <XAxis
                    dataKey="skill"
                    axisLine={{ stroke: '#e5e7eb' }}
                    tickLine={false}
                    tick={renderPositionTick}
                    interval={0}
                    height={110}
                  />
                  <YAxis hide domain={[0, (max) => max * 1.15]} />
                  <Tooltip />
                  <Bar
                    dataKey="totalShifts"
                    name="Total Shifts"
                    fill="#00C445"
                    radius={[4, 4, 0, 0]}
                    barSize={16}
                    onClick={(data) => setActiveWorkerPosition(data?.payload?.skill || data?.skill)}
                    cursor="pointer"
                  >
                    <LabelList
                      dataKey="totalShifts"
                      position="top"
                      offset={6}
                      formatter={(value) => value.toLocaleString()}
                      style={{ fontSize: 11, fill: '#374151' }}
                    />
                  </Bar>
                  <Bar
                    dataKey="assignedShifts"
                    name="Assigned Shifts"
                    fill="#1A73E8"
                    radius={[4, 4, 0, 0]}
                    barSize={16}
                    onClick={(data) => setActiveWorkerPosition(data?.payload?.skill || data?.skill)}
                    cursor="pointer"
                  >
                    <LabelList
                      dataKey="assignedShifts"
                      position="top"
                      offset={6}
                      formatter={(value) => value.toLocaleString()}
                      style={{ fontSize: 11, fill: '#374151' }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Existing Charts - Planning and Actuals */}
          <div className="bg-white rounded-lg border border-gray-200 p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-foreground">Shifts Planning Status</h2>
              {planningLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
            </div>
            <div className="flex items-center gap-6 mb-3">
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#00C445' }}></span> Confirm
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#E3EF41' }}></span> Tentative
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#EB5656' }}></span> Unassigned
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-800">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#F59E0B' }}></span> Unfilled
              </div>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm text-gray-500 mb-3">
                Showing data for: {getRangeLabel(planningMeta, planningData)}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={planningData} barGap={2} barCategoryGap="30%" margin={isNarrowScreen ? { top: 5, right: 10, left: 0, bottom: 5 } : undefined}>
                  <CartesianGrid vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={isNarrowScreen ? formatShiftsAxisDateCompact : formatShiftsAxisDate}
                    interval={isNarrowScreen ? 0 : undefined}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} width={isNarrowScreen ? 36 : undefined} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} content={<CustomTooltip />} />
                  <Bar dataKey="Confirmed" fill="#00C445" radius={[4, 4, 0, 0]} barSize={14} />
                  <Bar dataKey="Tentative" fill="#E3EF41" radius={[4, 4, 0, 0]} barSize={14} />
                  <Bar dataKey="Unassigned" fill="#EB5656" radius={[4, 4, 0, 0]} barSize={14} />
                  <Bar dataKey="Unfilled" fill="#F59E0B" radius={[4, 4, 0, 0]} barSize={14} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5 overflow-hidden">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-lg font-semibold text-foreground">Historic Actuals Recorded Status</h2>
              {actualsLoading && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" aria-label="Loading" />}
            </div>
            <div className="flex flex-col gap-1.5 mb-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#00C445' }}></span> As Schedule
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#E3EF41' }}></span> Different Actual Time
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#5292F9' }}></span> No Show
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: '#000000' }}></span> Not Submitted
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-800">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}></span> Unassigned
                </div>
              </div>
            </div>
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm text-gray-500 mb-3">
                Showing data for: {getRangeLabel(actualsMeta, actualsData)}
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={actualsData} barGap={2} barCategoryGap="30%" margin={isNarrowScreen ? { top: 5, right: 10, left: 0, bottom: 5 } : undefined}>
                  <CartesianGrid vertical={false} stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={isNarrowScreen ? formatShiftsAxisDateCompact : formatShiftsAxisDate}
                    interval={isNarrowScreen ? 0 : undefined}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: '#6b7280' }}
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280' }} width={isNarrowScreen ? 36 : undefined} />
                  <Tooltip cursor={{ fill: 'rgba(0,0,0,0.05)' }} content={<CustomTooltip />} />
                  <Bar dataKey="AsScheduled" name="As Scheduled" fill="#00C445" radius={[4, 4, 0, 0]} barSize={10} />
                  <Bar dataKey="DifferentTimes" name="Different Actual Time" fill="#E3EF41" radius={[4, 4, 0, 0]} barSize={10} />
                  <Bar dataKey="NoShow" name="No Show" fill="#5292F9" radius={[4, 4, 0, 0]} barSize={10} />
                  <Bar dataKey="NotSubmitted" name="Not Submitted" fill="#000000" radius={[4, 4, 0, 0]} barSize={10} />
                  <Bar dataKey="Unassigned" name="Unassigned" fill="rgba(0,0,0,0.5)" radius={[4, 4, 0, 0]} barSize={10} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
